import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useLinkPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Caption from '@zenbusiness/common-components/Caption';
import Text from '@zenbusiness/common-components/Text';
import PropTypes from 'prop-types';
import { useParams, Link as RouterLink } from 'react-router-dom';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';

import { readLastUpdatedDate } from '../../utils/date';
import { sortAsc } from '../../utils/sort';

const sortAscByCreatedDatetime = sortAsc('createdDatetime');

const StyledContainer = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(6)
}));

const StyledTitleGrid = styled(Grid)(({ theme }) => ({
  order: 1,
  marginRight: theme.spacing(1.5)
}));

const StyledStatusGrid = styled(Grid)(({ theme }) => ({
  order: 3,
  flexBasis: '100%',

  [theme.breakpoints.up('md')]: {
    order: 2,
    flexBasis: 'auto'
  }
}));

const StyledActionGrid = styled(Grid)(({ theme }) => ({
  marginLeft: 'auto',
  marginRight: theme.spacing(-2),
  order: 3,
  [theme.breakpoints.down('sm')]: {
    order: 2
  }
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  height: 1,
  background: theme.palette.focus30.main
}));

const StyledInnerCard = styled(Card)(({ theme }) => ({
  maxWidth: '350px',
  [theme.breakpoints.down('sm')]: {
    minWidth: '240px'
  },
  '& .MuiCardContent-root': {
    padding: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      height: 'auto'
    }
  },
  '& .MuiButton-root': {
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  '& .MuiDivider-root': {
    backgroundColor: '#f5f9fa'
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: 0,
  textTransform: 'none',
  color: theme.palette.peace40.main
}));

const StyledEmptyGrid = styled(Grid)(() => ({
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundSize: 'contain',
  width: '100%',
  height: '100%',
  minHeight: '150px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start'
}));

const NameserverCards = ({
  domain
}) => {
  const { businessEntityUuid } = useBusinessEntity();
  const { domainUuid } = useParams();
  const lastUpdated = readLastUpdatedDate(domain.domainNameservers);
  const {
    domainNameservers: domainNameserversAll,
    useZenbusinessNameservers
  } = domain;
  const domainNameservers = domainNameserversAll && domainNameserversAll.filter(
    ({ deleted }) => (!deleted)
  );

  // TODO: Change useLinkPressedCallback by a button_pressed - Clicks
  const handleEditClick = useLinkPressedCallback({
    elementName: 'edit_nameservers',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'domain_settings_page'
  });

  const nameserversUrl = `/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}/nameservers`;

  let cards;
  if (useZenbusinessNameservers) {
    cards = (
      <StyledEmptyGrid item>
        <Text size="lg">Using Zenbusiness nameservers.</Text>
      </StyledEmptyGrid>
    );
  } else if (!domainNameservers || domainNameservers.length <= 0) {
    cards = (
      <StyledEmptyGrid item>
        <Text size="lg">You haven&apos;t setup your Nameservers yet.</Text>
        <StyledButton
          color="primary"
          component={RouterLink}
          to={nameserversUrl}
        >
          Add Nameservers
        </StyledButton>
      </StyledEmptyGrid>
    );
  } else {
    cards = domainNameservers
      .sort(sortAscByCreatedDatetime)
      .map((nameserver) => (
        <Grid
          key={nameserver.domainNameserverUuid}
          item
          xs={12}
          sm={6}
          md={6}
          lg={3}
        >
          <StyledInnerCard elevation={0}>
            <CardContent>
              <Text size="sm">{nameserver.name}</Text>
            </CardContent>
          </StyledInnerCard>
        </Grid>
      ));
  }

  return (
    <Grid id="nameservers" item xs={12}>
      <StyledContainer>
        <Grid
          container
          item
          xs={12}
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-end"
        >
          <StyledTitleGrid item>
            <Text size="md">Nameservers</Text>
          </StyledTitleGrid>
          {lastUpdated && (
            <StyledStatusGrid item>
              <Caption size="lg" color="focus50">
                {`Last updated: ${lastUpdated}`}
              </Caption>
            </StyledStatusGrid>
          )}
          <StyledActionGrid item>
            <StyledButton
              color="primary"
              size="small"
              variant="text"
              onClick={handleEditClick}
              component={RouterLink}
              to={nameserversUrl}
            >
              {domainNameservers ? 'Edit' : 'Add'}
            </StyledButton>
          </StyledActionGrid>
        </Grid>
        <StyledDivider />
        <Grid
          container
          spacing={2}
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          pb={6}
        >
          {cards}
        </Grid>
      </StyledContainer>
    </Grid>
  );
};

/* eslint-disable react/forbid-prop-types */
NameserverCards.propTypes = {
  domain: PropTypes.object.isRequired
};
/* eslint-enable react/forbid-prop-types */

export default NameserverCards;
